import React, { memo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import cn from "src/features/utils/cn";
import { RotatingLines } from "react-loader-spinner";
import Button from "src/components/Buttons/Button";
import {
  ButtonColors,
  ButtonSizes,
} from "src/components/Buttons/buttons.types";
import supportService from "../services/supportService";
import { useNavigate } from "react-router-dom";
import { useAlert } from "@blaumaus/react-alert";
import useErrorHandling from "src/features/errors/hooks/useErrorHandling";
import { AxiosError } from "axios";

const createSupportSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("validationNameRequired")),
    email: z.string().email(t("validationEmailInvalid")),
    subject: z.string().min(1, t("validationSubjectRequired")),
    message: z.string().min(1, t("validationMessageRequired")),
  });

type SupportFormSchema = ReturnType<typeof createSupportSchema>;
export type SupportFormData = z.infer<SupportFormSchema>;

function SupportPage() {
  const { t } = useTranslation("support");
  const navigate = useNavigate();
  const alert = useAlert();
  const handleErrors = useErrorHandling();

  const supportSchema = createSupportSchema(t);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SupportFormData>({
    resolver: zodResolver(supportSchema),
  });

  const mutation = useMutation({
    mutationFn: (data: SupportFormData) =>
      supportService.createSupportTicket(data),
    onSuccess: () => {
      reset();
      alert.success(t("submitSuccess"));
      navigate("/");
    },
    onError: (error: AxiosError) => handleErrors(error),
  });

  const onSubmit = (data: SupportFormData) => mutation.mutate(data);

  return (
    <form
      className="flex p-10 w-full lg:max-w-sm z-1"
      onSubmit={handleSubmit(onSubmit)}
      aria-labelledby="supportHeading"
    >
      <div className="flex p-10 max-w-full card">
        <h2 id="supportHeading">{t("pageTitle")}</h2>
        <span className="text-muted">
          {t("pageDescription")}
        </span>
        <div className="flex flex-col gap-10 mt-20">
          <div className="flex gap-5">
            <label htmlFor="name" className="text-muted text-sm">
              {t("nameLabel")}
            </label>
            <input
              type="text"
              id="name"
              placeholder={t("namePlaceholder")}
              className={cn("blackInput", errors?.name && "border-danger")}
              aria-invalid={!!errors?.name}
              aria-describedby="nameError"
              {...register("name")}
            />
            {errors.name && (
              <span id="nameError" className="text-danger text-sm ml-auto">
                {errors.name.message}
              </span>
            )}
          </div>

          <div className="flex gap-5">
            <label htmlFor="email" className="text-muted text-sm">
              {t("emailLabel")}
            </label>
            <input
              type="email"
              id="email"
              placeholder={t("emailPlaceholder")}
              className={cn("blackInput", errors?.email && "border-danger")}
              aria-invalid={!!errors?.email}
              aria-describedby="emailError"
              {...register("email")}
            />
            {errors.email && (
              <span id="emailError" className="text-danger text-sm ml-auto">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="flex gap-5">
            <label htmlFor="subject" className="text-muted text-sm">
              {t("subjectLabel")}
            </label>
            <input
              type="text"
              id="subject"
              placeholder={t("subjectPlaceholder")}
              className={cn("blackInput", errors?.subject && "border-danger")}
              aria-invalid={!!errors?.subject}
              aria-describedby="subjectError"
              {...register("subject")}
            />
            {errors.subject && (
              <span id="subjectError" className="text-danger text-sm ml-auto">
                {errors.subject.message}
              </span>
            )}
          </div>

          <div className="flex gap-5">
            <label htmlFor="message" className="text-muted text-sm">
              {t("messageLabel")}
            </label>
            <textarea
              id="message"
              placeholder={t("messagePlaceholder")}
              rows={5}
              className={cn(
                "blackTextArea",
                errors?.message && "border-danger"
              )}
              aria-invalid={!!errors?.message}
              aria-describedby="messageError"
              {...register("message")}
            />
            {errors.message && (
              <span id="messageError" className="text-danger text-sm ml-auto">
                {errors.message.message}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-row justify-between gap-10 mt-40">
          <Button
            color={ButtonColors.GrayAndYellow}
            size={ButtonSizes.LG}
            onClick={() => reset()}
            className="flex-1"
            id="resetButton"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {t("resetBtn")}
          </Button>
          <Button
            color={ButtonColors.Yellow}
            size={ButtonSizes.LG}
            type="submit"
            className="flex-1"
            id="submitButton"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? (
              <RotatingLines
                strokeColor="#000"
                strokeWidth="5"
                animationDuration="0.75"
                width="20"
                visible={true}
              />
            ) : (
              t("submitBtn")
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default memo(SupportPage);
